import React, { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import { THEME } from "../config/theme"

const LoginPage: React.FC = () => {
  const { login } = useAuth()
  const navigate = useNavigate()

  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      // Read the form, not the React state. A password manager can fill these
      // fields without firing the events React listens for, leaving `username`
      // and `password` empty — the request then posts blank credentials and
      // the sign-in silently fails. FormData always reflects what the browser
      // actually put in the inputs.
      const form = new FormData(e.currentTarget)
      const submittedUsername = String(form.get("username") ?? "") || username
      const submittedPassword = String(form.get("password") ?? "") || password
      const user = await login(submittedUsername, submittedPassword)
      if (user.is_first_login) {
        navigate("/change-password")
      } else {
        navigate("/dashboard")
      }
    } catch (err) {
      // login() words each failure (wrong password, account off, INVI unreachable).
      setError(err instanceof Error ? err.message : "Couldn't log in. Try again.")
    } finally {
      setLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: "100%",
    height: "44px",
    border: `0.5px solid ${THEME.colors.neutral.light.border}`,
    borderRadius: THEME.borderRadius.md,
    padding: "0 12px",
    fontSize: "15px",
    fontFamily: THEME.fonts.primary,
    boxSizing: "border-box",
    outline: "none",
  }

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: THEME.colors.neutral.light.textPrimary,
    marginBottom: "6px",
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: THEME.colors.brandGradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: THEME.fonts.primary,
      }}
    >
      <div
        style={{
          width: "400px",
          background: THEME.colors.neutral.light.surface,
          border: `0.5px solid ${THEME.colors.neutral.light.border}`,
          borderRadius: THEME.borderRadius.lg,
          padding: "40px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "32px",
          }}
        >
          <img
            src="/lockup-on-light.png"
            alt="INVI Auto"
            style={{ width: 220, display: "block" }}
          />
        </div>

        <form onSubmit={handleSubmit}>
          <label style={labelStyle} htmlFor="username">Username</label>
          <input
            id="username"
            name="username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={{ ...inputStyle, marginBottom: "16px" }}
            autoComplete="username"
            required
          />

          <label style={labelStyle} htmlFor="password">Password</label>
          <div style={{ position: "relative" }}>
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ ...inputStyle, paddingRight: "60px" }}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                color: THEME.colors.navy,
                fontSize: "13px",
                fontWeight: 500,
                cursor: "pointer",
                padding: "4px 8px",
              }}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              height: "44px",
              background: THEME.colors.navy,
              color: THEME.colors.white,
              fontWeight: 500,
              fontSize: "14px",
              border: "none",
              borderRadius: THEME.borderRadius.md,
              marginTop: "24px",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
              fontFamily: THEME.fonts.primary,
            }}
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>

          <div style={{ textAlign: "center", marginTop: "16px" }}>
            <Link
              to="/forgot-password"
              style={{
                fontSize: "13px",
                color: THEME.colors.neutral.light.textSecondary,
                textDecoration: "none",
                cursor: "pointer",
              }}
            >
              Forgot password?
            </Link>
          </div>

          {error && (
            <div
              style={{
                background: "rgba(220, 38, 38, 0.08)",
                color: THEME.colors.semantic.danger,
                border: `0.5px solid rgba(220, 38, 38, 0.3)`,
                padding: "10px 12px",
                borderRadius: THEME.borderRadius.md,
                marginTop: "12px",
                fontSize: "13px",
              }}
            >
              {error}
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default LoginPage
